import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { Button, MenuItem, Select, Skeleton, Stack, Tooltip } from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState, useContext } from 'react';

import AddSavedMessageModal from '@features/savedMessages/components/AddSavedMessageModal/AddSavedMessageModal';
import { useGetSavedMessages } from '@features/savedMessages/hooks/useGetSavedMessages';
import { useGetEnterpriseSavedMessages } from '@features/savedMessages/hooks/useGetEnterpriseSavedMessages';
import { authContext } from '@staffedupusa/shared-components';

type SavedMessagesSelectProps = {
  setMessage: Dispatch<SetStateAction<string>>;
  message: string;
  company_id?: number;
};

const tooltipText =
  'Saving a message allows you to use it again later, streamlining the messaging process.';

const manageSavedMessagesUrl = '/employer/account/#Saved-Messages';
const manageSavedMessagesLabel = 'manageSavedMessages';

const SavedMessagesSelect: FC<SavedMessagesSelectProps> = ({
  setMessage,
  message,
  company_id,
}) => {
  const { authData } = useContext(authContext);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [isAddMessageModalOpen, setIsAddMessageModalOpen] = useState(false);

  const { savedMessages } = useGetSavedMessages({ enabled: authData?.user?.company ? true : false });
  const { savedEnterpriseMessages, isLoadingSavedEnterpriseMessages } = useGetEnterpriseSavedMessages({ enabled: authData?.user?.enterprise && company_id ? true : false, company_id });

  const handleTemplateSelect = (label: string) => {
    if (label === manageSavedMessagesLabel) {
      window.open(manageSavedMessagesUrl, '_blank');
      return;
    }
    setSelectedMessage(label);

    if (label !== '') {
      const messageTemplate = authData?.user?.company ? savedMessages?.object?.find(
        (savedMessage) => savedMessage.label === label) : savedEnterpriseMessages?.object?.find(
          (savedMessage) => savedMessage.label === label);

      if (messageTemplate) {
        return setMessage(messageTemplate.content);
      }
    }

    setMessage('');
  };
  useEffect(() => {
    if (selectedMessage !== '') {
      if (authData?.user?.company){
        const messageTemplate = savedMessages?.object?.find(
          (savedMessage) => savedMessage.label === selectedMessage,
        );
  
        if (messageTemplate) {
          return setMessage(messageTemplate.content);
        }
      } else if (authData?.user?.enterprise){
        const messageTemplate = savedEnterpriseMessages?.object?.find(
          (savedMessage) => savedMessage.label === selectedMessage,
        );

        if (messageTemplate) {
          return setMessage(messageTemplate.content);
        }
      }
    }
  }, [selectedMessage]);
  useEffect(() => {
    if (selectedMessage !== '') {
      if (authData?.user?.company) {
        const isSavedMessage = savedMessages?.object?.some(
          (savedMessage) => savedMessage.content === message,
        );
  
        if (!isSavedMessage) {
          setSelectedMessage('');
        }
      }else if(authData?.user?.enterprise){
        const isSavedMessage = savedEnterpriseMessages?.object?.some(
          (savedMessage) => savedMessage.content === message,
        );

        if (!isSavedMessage) {
          setSelectedMessage('');
        }
  
      }
    }
  }, [message]);
  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', sm: 'row' }}
      alignItems="center"
    >
      {authData?.user?.company  && (
        <Select
          value={selectedMessage}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          displayEmpty
          size="small"
          sx={{ flex: 1 }}
        >
          <MenuItem value="" selected>Select a saved message</MenuItem>
          {!savedMessages?.object?.length &&
            <MenuItem value="" disabled>No Saved Message</MenuItem>
          }
          {savedMessages?.object?.map((savedMessage) => (
            <MenuItem key={savedMessage.id} value={savedMessage.label}>
              {savedMessage.label}
            </MenuItem>
          ))}
          <MenuItem value={manageSavedMessagesLabel}>
            Manage saved messages
          </MenuItem>
        </Select>
      )}
      {authData?.user?.enterprise && (
        <Select
          value={selectedMessage}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          displayEmpty
          size="small"
          sx={{ flex: 1 }}
        >
          <MenuItem value="" selected>Select a saved message</MenuItem>
          {!savedEnterpriseMessages?.object?.length &&
            <MenuItem value="" disabled>No Saved Message</MenuItem>
          }
          {savedEnterpriseMessages?.object?.map((savedMessage) => (
            <MenuItem key={savedMessage.id} value={savedMessage.label}>
              {savedMessage.label}
            </MenuItem>
          ))}
        </Select>
      )}
      {isLoadingSavedEnterpriseMessages && (
        <Skeleton sx={{ flex: 1 }} variant="rectangular" height={30}/>
      )}
      <Stack spacing={2} direction="row" alignItems="center">
        <Button
          size="small"
          color="secondary"
          variant="outlined"
          startIcon={<MarkEmailReadIcon />}
          onClick={() => setIsAddMessageModalOpen(true)}
        >
          Save New Message
        </Button>
        <Tooltip title={tooltipText} placement="top" arrow>
          <HelpOutlineIcon color="secondary" fontSize="small" />
        </Tooltip>
      </Stack>
      <AddSavedMessageModal
        isOpen={isAddMessageModalOpen}
        handleClose={() => setIsAddMessageModalOpen(false)}
        initialMessage={message}
        company_id={company_id}
      />
    </Stack>
  );
};

export default SavedMessagesSelect;
